import React, { FC } from 'react'

import {
  DeepPartial,
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormWatch,
} from 'react-hook-form'
import { UseFormSetValue } from 'react-hook-form/dist/types/form'

import { TextField, Txt } from 'src/components/atoms'
import { DateTxt, UserDetailBasicInfoContentItem, UserKeywordsForm } from 'src/components/molecules'

import { useAuthority } from 'src/hooks'
import {
  EditUserBasicInfoFormEntity,
  WorksRoleType,
  InternalUserDetailEntity,
  ProfileEntity,
} from 'src/types'

type BaseProps = {
  user: InternalUserDetailEntity | ProfileEntity
  isUpdating: boolean
  className?: string
}
type BasicModeProps = { isEditMode: false }
type EditModeProps = {
  isEditMode: true
  worksRoles: WorksRoleType[]
  register: UseFormRegister<EditUserBasicInfoFormEntity>
  watch: UseFormWatch<EditUserBasicInfoFormEntity>
  setValue: UseFormSetValue<EditUserBasicInfoFormEntity>
  fields: FieldArrayWithId<DeepPartial<EditUserBasicInfoFormEntity>, 'keywords', 'id'>[]
  append: UseFieldArrayAppend<EditUserBasicInfoFormEntity, 'keywords'>
  remove: UseFieldArrayRemove
}

type Props = BaseProps & (BasicModeProps | EditModeProps)

export const InternalUserDetailBasicInfoContent: FC<Props> = ({
  user,
  isUpdating,
  isEditMode,
  className,
  ...otherProps
}) => {
  const { canManageUser, isSignInUser } = useAuthority()

  return (
    <div className={className}>
      <div>
        {isEditMode && (canManageUser || isSignInUser({ userId: user.id })) ? (
          <TextField
            {...(otherProps as EditModeProps).register('name')}
            className="tw-w-full"
            sizeType="l"
          />
        ) : (
          <Txt className="tw-break-all tw-font-bold !tw-text-s3">
            {isUpdating ? (otherProps as EditModeProps).watch('name') : user.name}
          </Txt>
        )}
        <Txt sizeType="l" className="tw-break-all tw-text-standardText-secondary tw-mt-s-2">
          {user.email}
        </Txt>
      </div>
      <ul className="tw-grid tw-gap-s2 tw-mt-s2">
        <li>
          <UserDetailBasicInfoContentItem title="ID">
            <Txt sizeType="l">{user.id}</Txt>
          </UserDetailBasicInfoContentItem>
        </li>
        <li>
          <UserDetailBasicInfoContentItem title="作成日">
            <DateTxt sizeType="l" date={user.createdAt} withTime />
          </UserDetailBasicInfoContentItem>
        </li>
        <li>
          <UserDetailBasicInfoContentItem title="キーワード">
            {isEditMode && canManageUser ? (
              <UserKeywordsForm
                register={(otherProps as EditModeProps).register}
                fields={(otherProps as EditModeProps).fields}
                append={(otherProps as EditModeProps).append}
                remove={(otherProps as EditModeProps).remove}
                className="tw-w-full"
              />
            ) : (
              <Txt sizeType="l" className="tw-break-all">
                {user.keywords.length > 0 ? user.keywords.join(', ') : '-'}
              </Txt>
            )}
          </UserDetailBasicInfoContentItem>
        </li>
      </ul>
    </div>
  )
}
